// What? A modal component for viewing AI generation prompts
// Why? To allow users to inspect the exact prompts used to generate document sections
// How? Uses JoyUI Modal with copy-to-clipboard functionality and loading states

import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  DialogTitle,
  Modal,
  ModalDialog,
  Stack,
  Textarea,
  Typography,
} from "@mui/joy";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";

interface PromptPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  prompt?: string;
  isLoading: boolean;
}

const PromptPreviewModal: React.FC<PromptPreviewModalProps> = ({
  isOpen,
  onClose,
  prompt,
  isLoading,
}) => {
  const [copySuccess, setCopySuccess] = useState(false);

  const handleCopy = async () => {
    if (prompt) {
      try {
        await navigator.clipboard.writeText(prompt);
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000); // Reset after 2 seconds
      } catch (err) {
        console.error("Failed to copy text:", err);
      }
    }
  };

  return (
    <Modal open={isOpen} onClose={onClose}>
      <ModalDialog sx={{ maxWidth: "800px", width: "90vw" }}>
        <DialogTitle>Generation Prompt</DialogTitle>

        <Stack spacing={2} sx={{ maxHeight: "70vh", overflow: "auto" }}>
          {isLoading ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1,
                py: 4,
              }}
            >
              <CircularProgress size="lg" />
              <Typography level="body-sm">Loading prompt...</Typography>
            </Box>
          ) : (
            <Textarea
              value={prompt}
              readOnly
              variant="outlined"
              minRows={12}
              maxRows={16}
              sx={{
                fontFamily: "monospace",
                fontSize: "sm",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                "& textarea": {
                  overflowY: "auto",
                  overflowX: "hidden",
                  resize: "none",
                },
              }}
            />
          )}

          <Stack
            direction="row"
            spacing={1}
            justifyContent="flex-end"
            sx={{ pt: 1, borderTop: "1px solid", borderColor: "neutral.200" }}
          >
            <Button
              variant="solid"
              color="primary"
              onClick={handleCopy}
              disabled={!prompt || isLoading}
              startDecorator={copySuccess ? <CheckIcon /> : <ContentCopyIcon />}
            >
              {copySuccess ? "Copied!" : "Copy to Clipboard"}
            </Button>
            <Button variant="outlined" color="neutral" onClick={onClose}>
              Close
            </Button>
          </Stack>
        </Stack>
      </ModalDialog>
    </Modal>
  );
};

export default PromptPreviewModal;
